import React, { useState, useEffect } from "react";
import {
  DataGrid,
  GridFooterContainer,
  GridPagination,
  GridRenderCellParams,
  GridSortModel
} from "@mui/x-data-grid";
import { useGetAllLeads } from "../service/LeadOverviewServiceWon";
import { useNavigate } from "react-router-dom";
import { getProductDisplayName, Product } from "types/product";
import { getLeadStatus } from "types/stateStatus";
import { getLeadTypeLabel, LeadType } from "types/leadType";
import { LeadOverview as LeadOverviewData } from "../service/dtos/index";
import _ from "lodash";
import LeadOverviewFilters from "./LeadOverviewFilters";
import LeadDetailSidebar from "./LeadDetailSidebar"; // Neue Sidebar-Komponente importieren
import "./../../../assets/styles/leadOverview/leadOverview.scss";
import CustomColumnHeader from "./CustomTableHeader";
import { getLeadStatusClass } from "../utils/leadOverview";
import { BlurredTypograhy } from "components/ui/typograhy";
import AddClientSidebar from "features/leadProviders/waitingListLeadV1/components/AddClientSidebar";

const LeadOverview = () => {
  interface RowData {
    id: string;
    leadNumber: string;
    product: number;
    location: string;
    soldDate: string;
    fullName: string;
    origin: number;
    link: string;
    status: number;
    assignedTo: string;
  }

  const [selectedStatusItems, setSelectedStatusItems] = useState<number[]>([]);
  const [selectedLeadTypeItems, setSelectedLeadTypeItems] = useState<number[]>([]);
  const [selectedProductItems, setSelectedProductItems] = useState<number[]>([]);
  const [nameSearchText, setNameSearchText] = useState<string>("");
  const [anfragenNumberSearchText, setAnfragenNumberSearchText] = useState<string>("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [rows, setRows] = useState<RowData[]>([]);
  const [sortModel, setSortModel] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedLead, setSelectedLead] = useState<RowData | null>(null);
  const [isCreateClient, setIsCreateClient] = useState(false);

  const CustomNoRowsOverlay = () => <div />;

  const { data: leadReceiptsInfo, isSuccess, isFetching } = useGetAllLeads(
    pageSize,
    page,
    nameSearchText,
    anfragenNumberSearchText,
    selectedStatusItems,
    selectedLeadTypeItems,
    selectedProductItems,
    sortModel.length > 0 ? sortModel[0].field : "soldDate",
    sortModel.length > 0 ? sortModel[0].sort : "desc"
  );

  const navigate = useNavigate();

  useEffect(() => {
    setLoading(isFetching);
    if (isSuccess && leadReceiptsInfo) {
      const rows = leadReceiptsInfo.leadOverviews.map((lead: LeadOverviewData) => ({
        id: lead.leadId,
        soldDate: lead.createdDateUtc,
        fullName: `${lead.lastName}, ${lead.firstName}`,
        origin: lead.type,
        status: lead.state,
        leadNumber: lead.leadNumber,
        link: lead.link,
        product: lead.product,
        location: `${lead.zipCode} ${lead.city}`,
        assignedTo: lead.assignedTo || "-",
        tasks: lead.tasks,
        phase: lead.phase,
        priority: lead.priority

      }));
      setRows(rows);
      setRowCount(leadReceiptsInfo.totalDataCount);
    }
  }, [isSuccess, leadReceiptsInfo, isFetching]);

  const handleNavigate = (link: string, type: LeadType, status: number) => {
    // Wenn Lead-Status 0 ist, navigiere zu /locked
    if (status === 0) {
      navigate("/waiting-list-lead-details/locked");
      return;
    }
    if (type === LeadType.WaitingList) {
      navigate("/waiting-list-lead-details/" + link);
    } else if (type === LeadType.WohnenImAlter) {
      navigate("/wohnen-im-alter-lead-details/" + link);
    } else if (type === LeadType.DeutschesSeniorenportal) {
      navigate("/deutsche-senioren-portal-lead-details/" + link);
    } else if (type === LeadType.Capacity) {
      navigate("/capacity-lead-details/" + link);
    } else if (type === LeadType.EntlassManager) {
      window.location.href = link;
    } else if (type === LeadType.Import) {
      navigate("/import-lead-details/" + link);
    }
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    setPage(0);
  };

  const columns = [
    {
      field: "leadNumber",
      headerName: "Anfrage",
      flex: 0.06,
      renderHeader: () => (
        <CustomColumnHeader title="Anfrage" field="leadNumber" sortModel={sortModel} />
      ),
      sortable: false
    },
    {
      field: "soldDate",
      headerName: "Anfrage-Datum",
      renderHeader: () => (
        <CustomColumnHeader title="Anfrage-Datum" field="soldDate" sortModel={sortModel} />
      ),
      flex: 0.14,
      renderCell: (params: GridRenderCellParams<Date>) =>
        new Date(params.value as Date).toLocaleString("de-DE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        }),
      sortable: true
    },
    {
      field: "fullName",
      headerName: "Name",
      flex: 0.14,
      renderHeader: () => (
        <CustomColumnHeader title="Name" field="fullName" sortModel={sortModel} />
      ),
      renderCell: (params: GridRenderCellParams<string>) => {
        // Wenn Status 0, dann wird der Name unscharf dargestellt und es erscheint ein Schloss-Icon
        if (params.row.status === 0) {
          return (
            <span className="blurred-name">
              <BlurredTypograhy defaulValue={params.value} />
            </span>
          );
        }
        // Falls der Name leer erscheint
        if (params.value === ",  ") {
          return <BlurredTypograhy />;
        }
        return params.value;
      }
    },
    {
      field: "product",
      renderHeader: () => (
        <CustomColumnHeader title="Bedarf" field="product" sortModel={sortModel} />
      ),
      renderCell: (params: any) => <span>{getProductDisplayName(params.value)}</span>,
      headerName: "Bedarf",
      flex: 0.14,
      sortable: false
    },
    {
      field: "location",
      renderHeader: () => (
        <CustomColumnHeader title="Bedarfsort" field="location" sortModel={sortModel} />
      ),
      renderCell: (params: GridRenderCellParams<string>) => {
        if (params.value === " ") {
          return <BlurredTypograhy defaulValue="Berlin" />;
        }
        return params.value;
      },
      headerName: "Bedarfsort",
      flex: 0.1,
      sortable: false
    },
    {
      field: "origin",
      renderHeader: () => (
        <CustomColumnHeader title="Herkunft" field="origin" sortModel={sortModel} />
      ),
      headerName: "Herkunft",
      flex: 0.1,
      renderCell: (params: any) => <span>{getLeadTypeLabel(params.value)}</span>,
      sortable: false
    },
    {
      field: "phase",
      headerName: "Phase",
      flex: 0.1,
      renderHeader: () => (
        <CustomColumnHeader title="Phase" field="phase" sortModel={sortModel} />
      ),
      renderCell: (params: any) => <span>{params.value}</span>,
      sortable: false
    },
    {
      field: "tasks",
      headerName: "Aufgaben",
      flex: 0.05,
      renderHeader: () => (
        <CustomColumnHeader title="Aufgaben" field="tasks" sortModel={sortModel} />
      ),
      renderCell: (params: any) => {
        const taskClass = params.value === 0 ? "lead-overview-no-tasks" : "lead-overview-open-tasks";
        return (
          <div className={taskClass}>
            {params.value}
          </div>
        );
      }
    },
    {
      field: "status",
      renderHeader: () => (
        <CustomColumnHeader title="Status" field="Status" sortModel={sortModel} />
      ),
      headerName: "Status",
      flex: 0.1,
      sortable: false,
      renderCell: (params: any) => {
        const statusClass = getLeadStatusClass(params.value);
        return (
          <span className={statusClass}>
            {getLeadStatus(params.value)}
          </span>
        );
      }
    },
    {
      field: "link",
      headerName: "Zum Kontakt",
      renderHeader: () => (
        <CustomColumnHeader title="Zum Kontakt" field="link" sortModel={sortModel} />
      ),
      flex: 0.09,
      sortable: false,
      renderCell: (params: any) => (
        <button
          className="to-client"
          onClick={() => handleNavigate(params.value, params.row.origin, params.row.status)}
        >
          <span className="icons-right-open pr-5"></span>
          <span className="icons-user"></span>
        </button>
      )
    }
  ];

  const CustomFooter = () => (
    <GridFooterContainer>
      <GridPagination
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
        labelRowsPerPage="Klienten pro Seite:"
        placeholder=""
        onPointerEnterCapture={() => { }}
        onPointerLeaveCapture={() => { }}
      />
    </GridFooterContainer>
  );

  return (
    <div className="detail-layout-2">
      <div className="lead-container">
        <h1 className="text-blue mb-50">Klienten verwalten</h1>
        <button onClick={() => setIsCreateClient(true)} className="kanban-switch-green">
          <i className="fa-solid fa-plus"></i> Klient erstellen
        </button>
        <LeadOverviewFilters
          setPage={setPage}
          onStatusChange={(statuses: number[]) => setSelectedStatusItems(statuses)}
          onLeadTypeChange={(leadTypes: LeadType[]) => setSelectedLeadTypeItems(leadTypes)}
          onProductChange={(products: Product[]) => setSelectedProductItems(products)}
          onNameSearchChange={(searchText: string) => setNameSearchText(searchText)}
          onAnfragenNumberSearchChange={(searchText: string) => setAnfragenNumberSearchText(searchText)}
        />

        <div className="data-grid-container">
          <DataGrid
            rows={rows}
            columns={columns}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowCount={rowCount}
            autoHeight={true}
            loading={loading}
            rowHeight={45}
            sortingOrder={["asc", "desc"]}
            rowsPerPageOptions={[5, 10, 20]}
            paginationMode="client"
            sortingMode="client"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            onRowClick={(params) => setSelectedLead(params.row)}
            components={{
              Footer: CustomFooter,
              NoRowsOverlay: CustomNoRowsOverlay
            }}
            disableSelectionOnClick
            disableColumnMenu
          />
        </div>

        {selectedLead && (
          <LeadDetailSidebar selectedLead={selectedLead} onClose={() => setSelectedLead(null)} />
        )}

        {isCreateClient && (
          <AddClientSidebar
            onClose={() => setIsCreateClient(false)}
            onSave={() => setIsCreateClient(false)}
          />
        )}
      </div>
    </div>
  );
};

export default LeadOverview;
